import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationsApi, Notification } from '../../lib/api/notifications.api';
import { formatDateTime } from '../../lib/utils';
import Loader from './Loader';
import { useSSE } from '../../contexts/SSEContext';

const NotificationsDropdown: React.FC = () => {
        const { isConnected, addHandlers, removeHandlers } = useSSE();
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showRead, setShowRead] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [totalNotifications, setTotalNotifications] = useState(0);
    const isLoadingRef = useRef(false);
    const lastLoadTimeRef = useRef(0);
    const MIN_LOAD_INTERVAL = 2000; // Mínimo 2 segundos entre solicitudes

    // Funciones de carga (declaradas antes de usarlas)
    const loadNotifications = useCallback(async () => {
        const now = Date.now();
        const timeSinceLastLoad = now - lastLoadTimeRef.current;

        if (isLoadingRef.current || timeSinceLastLoad < MIN_LOAD_INTERVAL) {
            console.log('🔔 NotificationsDropdown: Solicitud ignorada (cargando o muy reciente)');
            return;
        }

        console.log('🔔 NotificationsDropdown: loadNotifications() iniciado, showRead:', showRead);
        try {
            isLoadingRef.current = true;
            setLoading(true);
            lastLoadTimeRef.current = now;

            const response = await notificationsApi.getNotifications(showRead);
                        if (response.success && response.data) {
                                setNotifications(response.data.notifications);
                setUnreadCount(response.data.unreadCount || 0);
                setTotalNotifications(response.data.total || 0);
            }
        } catch (error) {
            console.error('Error al cargar notificaciones:', error);
            if ((error as any).message?.includes('429') || (error as any).message?.includes('Too many requests')) {
                                await new Promise(resolve => setTimeout(resolve, 5000));
            }
        } finally {
            isLoadingRef.current = false;
            setLoading(false);
        }
    }, [showRead]);

    const loadUnreadCount = useCallback(async () => {
        const now = Date.now();
        const timeSinceLastLoad = now - lastLoadTimeRef.current;

        if (isLoadingRef.current || timeSinceLastLoad < MIN_LOAD_INTERVAL) {
            return;
        }

        try {
            isLoadingRef.current = true;
            lastLoadTimeRef.current = now;

            const result = await notificationsApi.getUnreadCount();
                                    setUnreadCount(result.unreadCount);
        } catch (error) {
            console.error('Error al cargar contador de no leídas:', error);
            if ((error as any).message?.includes('429') || (error as any).message?.includes('Too many requests')) {
                                await new Promise(resolve => setTimeout(resolve, 5000));
            }
        } finally {
            isLoadingRef.current = false;
        }
    }, []);

    // Log cuando cambia el contador
    useEffect(() => {
            }, [unreadCount]);

    // Cargar notificaciones al abrir el dropdown
    useEffect(() => {
        if (isOpen) {
            loadNotifications();
        }
    }, [isOpen, showRead]);

    // Cargar contador de no leídas periódicamente
    useEffect(() => {
                loadUnreadCount();

        // Solo usar polling si SSE no está conectado
        if (!isConnected) {
            const interval = setInterval(loadUnreadCount, 120000);
            return () => clearInterval(interval);
        } else {
                    }
    }, [isConnected]);

    // Escuchar eventos SSE para actualizaciones en tiempo real
    useEffect(() => {
        if (!isConnected) {
                        return;
        }

        
        // Escuchar nuevas notificaciones
        const handleNotificationCreated = () => {
                        loadUnreadCount();
            // Si el dropdown está abierto, recargar notificaciones
            if (isOpen) {
                loadNotifications();
            }
        };

        // Escuchar cuando se marca una notificación como leída
        const handleNotificationMarkedAsRead = () => {
                        loadUnreadCount();
        };

        // Escuchar cuando se marcan todas como leídas
        const handleAllMarkedAsRead = () => {
                        setUnreadCount(0);
            if (isOpen && !showRead) {
                loadNotifications();
            }
        };

        // Escuchar cuando se elimina una notificación
        const handleNotificationDeleted = () => {
                        loadUnreadCount();
            if (isOpen) {
                loadNotifications();
            }
        };

        // Añadir handlers específicos de notificaciones
        addHandlers({
            onNotificationCreated: handleNotificationCreated,
            onNotificationMarkedAsRead: handleNotificationMarkedAsRead,
            onAllNotificationsMarkedAsRead: handleAllMarkedAsRead,
            onNotificationDeleted: handleNotificationDeleted
        });

        return () => {
                        removeHandlers();
        };
    }, [isConnected, isOpen, showRead, addHandlers, removeHandlers, loadUnreadCount, loadNotifications]);

    // Cerrar dropdown cuando se hace clic fuera
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handlers de eventos de UI
    const handleMarkAsRead = async (notificationId: string) => {
        try {
            await notificationsApi.markAsRead(notificationId);
            // Si no se están mostrando las leídas, activar el checkbox automáticamente y recargar
            if (!showRead) {
                setShowRead(true);
                // Recargar explícitamente después de cambiar el estado
                setTimeout(() => {
                    lastLoadTimeRef.current = 0; // Forzar recarga
                    loadNotifications();
                }, 0);
            } else {
                // Si ya se muestran las leídas, recargar las notificaciones
                await loadNotifications();
            }
        } catch (error) {
            console.error('Error al marcar como leída:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await notificationsApi.markAllAsRead();
            // Si no se están mostrando las leídas, activar el checkbox automáticamente
            if (!showRead) {
                setShowRead(true);
                // Recargar explícitamente después de cambiar el estado
                setTimeout(() => {
                    lastLoadTimeRef.current = 0; // Forzar recarga
                    loadNotifications();
                }, 0);
            } else {
                // Si ya se muestran las leídas, recargar las notificaciones
                await loadNotifications();
            }
        } catch (error) {
            console.error('Error al marcar todas como leídas:', error);
        }
    };

    const handleDelete = async (notificationId: string) => {
        try {
            await notificationsApi.deleteNotification(notificationId);
            await loadNotifications();
        } catch (error) {
            console.error('Error al eliminar notificación:', error);
        }
    };

    const handleDeleteRead = async () => {
        try {
            await notificationsApi.deleteReadNotifications();
            await loadNotifications();
        } catch (error) {
            console.error('Error al eliminar notificaciones leídas:', error);
        }
    };

    const handleDeleteAll = async () => {
        if (window.confirm('¿Estás seguro de que quieres eliminar todas las notificaciones?')) {
            try {
                await notificationsApi.deleteAllNotifications();
                await loadNotifications();
            } catch (error) {
                console.error('Error al eliminar todas las notificaciones:', error);
            }
        }
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'INFO':
                return (
                    <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                );
            case 'WARNING':
                return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                );
            case 'ERROR':
                return (
                    <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                );
            case 'SUCCESS':
                return (
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                );
            case 'TIME_ENTRY':
                return (
                    <svg className="w-5 h-5 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                );
            case 'OVERTIME_ALERT':
                return (
                    <svg className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                );
            case 'ABSENCE_REQUEST':
                return (
                    <svg className="w-5 h-5 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2a1 1 0 000 2h6a1 1 0 100-2H7zm6 7a1 1 0 011 1v3a1 1 0 11-2 0v-3a1 1 0 011-1zm-3 3a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm-3-1a1 1 0 011 1v2a1 1 0 11-2 0v-2a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                );
            default:
                return (
                    <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                );
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => {
                                        setIsOpen(!isOpen);
                }}
                className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/4 -translate-y-1/4 bg-red-600 rounded-full">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl z-50 max-h-[600px] flex flex-col">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-900">
                                Notificaciones
                                {unreadCount > 0 && (
                                    <span className="ml-2 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded-full">
                                        {unreadCount} nuevas
                                    </span>
                                )}
                            </h3>
                            <div className="flex items-center space-x-2">
                                <label className="flex items-center space-x-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showRead}
                                        onChange={(e) => setShowRead(e.target.checked)}
                                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                    />
                                    <span className="text-xs text-gray-600">Leídas</span>
                                </label>
                            </div>
                        </div>
                        <div className="flex items-center justify-between space-x-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllAsRead}
                                    className="text-xs text-primary-600 hover:text-primary-800"
                                >
                                    Marcar todas como leídas
                                </button>
                            )}
                            {totalNotifications > 0 && (
                                <div className="flex items-center space-x-2">
                                    {showRead && (
                                        <button
                                            onClick={handleDeleteRead}
                                            className="text-xs text-gray-600 hover:text-gray-800"
                                        >
                                            Eliminar leídas
                                        </button>
                                    )}
                                    <button
                                        onClick={handleDeleteAll}
                                        className="text-xs text-red-600 hover:text-red-800"
                                    >
                                        Eliminar todas
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Notificaciones */}
                    <div className="overflow-y-auto max-h-[500px]">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader />
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 px-4">
                                <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                </svg>
                                <p className="text-sm text-gray-500 text-center">
                                    No tienes notificaciones
                                </p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {notifications.map((notification) => (
                                    <div
                                        key={notification.id}
                                        className={`px-4 py-3 hover:bg-gray-50 transition-colors ${
                                            !notification.readAt ? 'bg-blue-50' : ''
                                        }`}
                                    >
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 mt-0.5">
                                                {getNotificationIcon(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <p className={`text-sm font-medium ${
                                                            !notification.readAt ? 'text-gray-900' : 'text-gray-600'
                                                        }`}>
                                                            {notification.title}
                                                        </p>
                                                        <p className={`text-sm mt-1 ${
                                                            !notification.readAt ? 'text-gray-700' : 'text-gray-500'
                                                        }`}>
                                                            {notification.message}
                                                        </p>
                                                        <p className="text-xs text-gray-400 mt-1">
                                                            {formatDateTime(notification.createdAt)}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center space-x-1 ml-2">
                                                        {!notification.readAt && (
                                                            <button
                                                                onClick={() => handleMarkAsRead(notification.id)}
                                                                className="p-1 text-gray-400 hover:text-primary-600"
                                                                title="Marcar como leída"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleDelete(notification.id)}
                                                            className="p-1 text-gray-400 hover:text-red-600"
                                                            title="Eliminar"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsDropdown;